import VueRouter from 'vue-router';
import {
    getLanguageForCountry,
    ipCountryQuery,
    languageQuery,
} from '@gig/magic-common';
import { config } from '../../config';
import { LANGUAGE_COMMAND_CHANGE } from '@gig/magic-messages';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { Language } from '@gig/magic-sdk/dist/adapter/users/language';
import { RouteName } from '../../../routes/routes.constants';

const updateLang = (path: string, lang: string) => {
    const langRegExp = /^\/[a-z]{2}(\/|$)/;
    return langRegExp.test(path)
        ? path.replace(langRegExp, `/${lang}$1`)
        : `/${lang}${path}`;
};

/**
 * Checks the current path language segment, if it doesn't
 * exist then it prepends the current language.
 *
 * Also synchronizes the current language with the store.
 */
export const initLanguageGuard = (router: VueRouter) => {
    router.beforeEach(async (to, _, next) => {
        const countryCode = await ipCountryQuery();
        const langForCountry = getLangForCountry(countryCode);
        const path = to.redirectedFrom || to.fullPath;
        const currentLanguage = await languageQuery();

        if (!to.params.language) {
            return next({
                path: `/${currentLanguage}${path}`,
                replace: true,
            });
        }

        if (
            countryCode !== Country.SWEDEN &&
            to.params.language === Language.SWEDISH
        ) {
            return next({
                name: RouteName.PAGE404,
                params: {
                    language: langForCountry,
                },
                replace: true,
            });
        }

        const isValidLanguage = config.languages.available.includes(
            to.params.language,
        );

        const countryIsLocked =
            countryCode &&
            config.countries.lockedCountries.includes(countryCode);

        if (
            !isValidLanguage ||
            (countryIsLocked && to.params.language !== langForCountry)
        ) {
            next({
                path: updateLang(path, langForCountry),
                replace: true,
            });
            return;
        }

        return next();
    });

    router.afterEach(async (to) => {
        const newLanguage = to.params.language;
        const oldLanguage = await languageQuery();
        if (newLanguage !== oldLanguage) {
            window.commandBus.publish(LANGUAGE_COMMAND_CHANGE, newLanguage);
        }
    });
};

function getLangForCountry(country: Country): Language | string {
    if (!country) {
        return config.languages.default;
    }
    const langForCountry = getLanguageForCountry(country);

    return config.languages.available.includes(langForCountry)
        ? langForCountry
        : config.languages.default;
}
